import './SegmentationMapping.scss';
import cn from 'classnames';
import { ungzip } from 'pako';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { base64ToUint8Array } from '@blisspointmedia/bpm-types/dist/binaryUtils';
import {
  attachBillingSegmentArrays,
  BILLING_SEGMENT_NAMES,
  BillingSegmentDefinitions,
  SegmentationMappingLabelRule
} from '@blisspointmedia/bpm-types/dist/SegmentationMapping';
import { Page } from '@Components/Page';
import { PAGE_STATE } from '@Components/Page/page-constants';
import { useSetError } from '../../redux/modals';
import {
  awaitJSON,
  awaitText,
  fetchCometSelfInvoke,
  SegmentationMappingLambdaFetch
} from '../../utils/fetch-utils';
import { useCompanyInfo } from '../../utils/hooks/useCompanyInfo';
import { useTabbedNav } from '../../utils/hooks/useNav';
import { CustomSegments } from './CustomSegments';
import { LabelingRules } from './LabelingRules';
import {
  BillingMappingLabelRule,
  CustomSegmentsData,
  SegmentRow
} from './segmentation-mapping-constants';
import { SegmentationIdCreation } from './SegmentationIdCreation';
import { SegmentationLabeling } from './SegmentationLabeling';
import { sortCustomSegments } from './segmentationMappingUtils';

export type { CustomSegmentsData } from './segmentation-mapping-constants';

const PageTab = {
  ID_GENERATION: 'id-generation',
  LABELING: 'labeling',
  CUSTOM_SEGMENTS: 'custom-segments',
  LABELING_RULES: 'labeling-rules'
} as const;
type PageTabType = (typeof PageTab)[keyof typeof PageTab];

const NAVS = [
  { label: 'Labeling', key: PageTab.LABELING },
  { label: 'Labeling Rules', key: PageTab.LABELING_RULES },
  { label: 'Custom Segments', key: PageTab.CUSTOM_SEGMENTS },
  { label: 'ID Generation', key: PageTab.ID_GENERATION }
];

export const SegmentationMapping = () => {
  const [pendingFetchCount, setPendingFetchCount] = useState(0);
  const loading = pendingFetchCount > 0;
  const { tab, goToTab } = useTabbedNav<PageTabType>({
    baseURL: 'segmentation-mapping',
    defaultKey: PageTab.LABELING,
    navs: NAVS.map(nav => nav.key)
  });

  const { cid } = useCompanyInfo();
  const setError = useSetError();

  const activeCid = useRef(cid);
  useEffect(() => {
    activeCid.current = cid;
  }, [cid]);

  const inflightFetches = useRef<Record<string, string>>({});
  const runGuarded = useCallback(
    (key: string, fetcher: () => Promise<unknown>) => {
      if (!cid || inflightFetches.current[key] === cid) return;
      inflightFetches.current[key] = cid;
      fetcher().finally(() => {
        if (inflightFetches.current[key] === cid) {
          delete inflightFetches.current[key];
        }
      });
    },
    [cid]
  );

  const [rawData, setRawData] = useState<SegmentRow[] | null>(null);
  const [dataGranularity, setDataGranularity] = useState<
    'ad' | 'ad_group' | 'campaign' | undefined
  >();

  const fetchRawData = useCallback(async () => {
    if (!cid) return;
    if (cid === 'tinuititest') {
      setRawData([]);
      return;
    }
    try {
      setPendingFetchCount(n => n + 1);

      if (import.meta.env.DEV) {
        console.info('In development mode, fetching getRawPlatformData from non-comet endpoint');
        const res = await SegmentationMappingLambdaFetch('/getRawPlatformData', {
          params: {
            company: cid,
            enableBillingSegments: true
          }
        });
        const parsedRes = await awaitJSON<SegmentRow[]>(res);
        const parsedData = parsedRes.map(row => {
          if (!row.billing_seg_map) return row;
          try {
            return { ...row, billing_seg_map: JSON.parse(row.billing_seg_map) };
          } catch {
            return { ...row, billing_seg_map: undefined };
          }
        });

        if (activeCid.current !== cid) return;
        setRawData(parsedData);
      } else {
        // GET: comet self-invoke route
        const lambdaResult = await fetchCometSelfInvoke<
          { company: string; enableBillingSegments: boolean },
          SegmentRow[]
        >(
          SegmentationMappingLambdaFetch,
          '/getRawPlatformData',
          { company: cid, enableBillingSegments: true },
          { method: 'GET', noTimeout: true }
        );
        if (activeCid.current !== cid) return;
        setRawData(lambdaResult);
      }
    } catch (e) {
      setError({ message: e.message, reportError: e });
    } finally {
      setPendingFetchCount(n => n - 1);
    }
  }, [cid, setError]);

  const [customAndBillingSegments, setCustomAndBillingSegments] = useState<
    CustomSegmentsData[] | undefined
  >();
  const [customSegments, setCustomSegments] = useState<CustomSegmentsData[] | undefined>();

  useEffect(() => {
    if (rawData) return;
    runGuarded('rawData', fetchRawData);
  }, [rawData, runGuarded, fetchRawData]);

  const fetchCustomSegments = useCallback(async () => {
    try {
      setPendingFetchCount(n => n + 1);
      const res = await SegmentationMappingLambdaFetch('/getCustomSegments', {
        params: { company: cid }
      });
      const data = await awaitJSON(res);
      if (activeCid.current !== cid) return;
      setCustomSegments(sortCustomSegments(data));
      const dataWithBillingSegments = [
        ...data,
        ...BILLING_SEGMENT_NAMES.map(segmentName => ({
          segmentId: null,
          segmentName,
          values: BillingSegmentDefinitions[segmentName].map(value => ({
            valueId: null,
            valueName: value
          }))
        }))
      ];
      setCustomAndBillingSegments(sortCustomSegments(dataWithBillingSegments));
    } catch (e) {
      setError({ message: e.message, reportError: e });
    } finally {
      setPendingFetchCount(n => n - 1);
    }
  }, [cid, setError]);

  useEffect(() => {
    if (customSegments) return;
    runGuarded('customSegments', fetchCustomSegments);
  }, [customSegments, runGuarded, fetchCustomSegments]);

  const fetchDataGranularity = useCallback(async () => {
    try {
      setPendingFetchCount(n => n + 1);
      const res = await SegmentationMappingLambdaFetch('/getGranularity', {
        params: {
          company: cid
        }
      });
      const granularity = await awaitJSON(res);
      if (activeCid.current !== cid) return;
      setDataGranularity(granularity);
    } catch (e) {
      setError({ message: e.message, reportError: e });
    } finally {
      setPendingFetchCount(n => n - 1);
    }
  }, [cid, setError]);

  useEffect(() => {
    if (dataGranularity) return;
    runGuarded('granularity', fetchDataGranularity);
  }, [dataGranularity, runGuarded, fetchDataGranularity]);

  const [labelingRules, setLabelingRules] = useState<SegmentationMappingLabelRule[] | undefined>();
  const [billingLabelingRules, setBillingLabelingRules] = useState<
    BillingMappingLabelRule[] | undefined
  >();

  const fetchGzippedRules = useCallback(
    async <T,>(endpoint: string): Promise<T[]> => {
      try {
        setPendingFetchCount(n => n + 1);
        const res = await SegmentationMappingLambdaFetch(endpoint, {
          params: {
            company: cid
          }
        });
        const resTextZippedBase64 = await awaitText(res);
        if (resTextZippedBase64.startsWith('"')) {
          const resText = ungzip(base64ToUint8Array(resTextZippedBase64.replaceAll('"', '')), {
            toText: true
          });
          return JSON.parse(resText);
        }
        return JSON.parse(resTextZippedBase64);
      } catch (e) {
        setError({ message: e.message, reportError: e });
        return [];
      } finally {
        setPendingFetchCount(n => n - 1);
      }
    },
    [cid, setError]
  );

  useEffect(() => {
    if (!cid) return;
    setRawData(null);
    setCustomSegments(undefined);
    setCustomAndBillingSegments(undefined);
    setDataGranularity(undefined);
    setLabelingRules(undefined);
    setBillingLabelingRules(undefined);
  }, [cid]);

  useEffect(() => {
    if (labelingRules) return;
    runGuarded('labelingRules', async () => {
      const rules = await fetchGzippedRules<SegmentationMappingLabelRule>(
        '/getSegmentLabelingRules'
      );
      if (activeCid.current !== cid) return;
      setLabelingRules(rules);
    });
  }, [cid, labelingRules, runGuarded, fetchGzippedRules]);

  useEffect(() => {
    if (billingLabelingRules) return;
    runGuarded('billingLabelingRules', async () => {
      const rules = await fetchGzippedRules<BillingMappingLabelRule>('/getBillingLabelingRules');
      if (activeCid.current !== cid) return;
      setBillingLabelingRules(rules);
    });
  }, [cid, billingLabelingRules, runGuarded, fetchGzippedRules]);

  const segLabelingData = useMemo(() => {
    if (!rawData) {
      return rawData;
    }
    return attachBillingSegmentArrays(rawData);
  }, [rawData]);

  return (
    <Page
      pageType='Segmentation Mapping'
      state={loading ? PAGE_STATE.LOADING : undefined}
      title='Segmentation Mapping'
      navs={NAVS}
      selectedNav={tab}
      onNav={key => goToTab(key as PageTabType)}
    >
      <div className='segmentation-mapping-page'>
        <div className={cn('segmentation-tab-pane', { 'is-hidden': tab !== PageTab.LABELING })}>
          <SegmentationLabeling
            data={segLabelingData}
            fetchRawData={fetchRawData}
            labelingRules={labelingRules}
            billingLabelingRules={billingLabelingRules}
            customSegments={customAndBillingSegments}
            dataGranularity={dataGranularity ?? 'campaign'}
          />
        </div>
        <div
          className={cn('segmentation-tab-pane', { 'is-hidden': tab !== PageTab.LABELING_RULES })}
        >
          <LabelingRules
            data={rawData}
            rules={labelingRules}
            billingRules={billingLabelingRules}
            customSegments={customAndBillingSegments ?? []}
            onSave={(rules, billingRules) => {
              setLabelingRules(rules);
              setBillingLabelingRules(billingRules);
            }}
            dataGranularity={dataGranularity ?? 'campaign'}
          />
        </div>
        {tab === PageTab.CUSTOM_SEGMENTS && (
          <CustomSegments
            data={customAndBillingSegments}
            setData={setCustomSegments}
            dataGranularity={dataGranularity ?? 'campaign'}
            setDataGranularity={setDataGranularity}
            onDeleteSegments={() => {
              setLabelingRules(undefined);
              setBillingLabelingRules(undefined);
            }}
          />
        )}
        {tab === PageTab.ID_GENERATION && (
          <SegmentationIdCreation customSegments={customSegments} />
        )}
      </div>
    </Page>
  );
};
